import React, { forwardRef, useEffect } from "react";
import { StyledCheckboxLabel } from "./style";
import { Icon } from "components/icon/Icon";
import { LottieLoading } from "components/lottie/LottieLoading";

/**
 * 체크박스 컴포넌트 속성
 */
interface CheckboxProps {
  ref?: React.ForwardedRef<HTMLInputElement>;
  checked: boolean;
  handleChecked?: (checked: boolean) => void;
  id?: string;
  name?: string;
  className?: string;
  label?: string;
  mode?: "left" | "right";
  size?: "large" | "small";
  color?: "primary" | "red-0";
  isLoading?: boolean;
  isHovered?: boolean;
  disabled?: boolean;
  defaultChecked?: boolean;
  /** 이벤트 핸들러 */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** 이벤트 핸들러 */
  onClick?: (e: React.MouseEvent<HTMLLabelElement>) => void;
  /** 이벤트 핸들러 */
  onMouseDown?: (e: React.MouseEvent<HTMLLabelElement>) => void;
  /** 이벤트 핸들러 */
  onMouseUp?: (e: React.MouseEvent<HTMLLabelElement>) => void;
  /** 이벤트 핸들러 */
  onMouseEnter?: (e: React.MouseEvent<HTMLLabelElement>) => void;
  /** 이벤트 핸들러 */
  onMouseLeave?: (e: React.MouseEvent<HTMLLabelElement>) => void;
  /** 마운트 핸들러 */
  onMount?: () => void;
  /** 언마운트 핸들러 */
  onUnmount?: () => void;
}

/**
 * Checkbox component
 * @author hc_yang
 * @param {CheckboxProps} param
 * @returns JSX.Element
 */
export const Checkbox = forwardRef(
  (
    {
      checked,
      handleChecked,
      id,
      name,
      label,
      className,
      mode = "left",
      size = "large",
      color = "primary",
      isLoading = false,
      isHovered = false,
      disabled = false,
      defaultChecked,
      onChange,
      onClick,
      onMouseDown,
      onMouseUp,
      onMouseEnter,
      onMouseLeave,
      onMount,
      onUnmount,
    }: CheckboxProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    useEffect(() => {
      onMount && onMount();
      return () => {
        onUnmount && onUnmount();
      };
    }, []);
    return (
      <StyledCheckboxLabel
        $color={color}
        $size={size}
        $mode={mode}
        $hovered={isHovered}
        $disabled={disabled}
        className={className}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <input
          ref={ref}
          type="checkbox"
          name={name}
          id={id}
          disabled={disabled}
          checked={checked}
          onChange={(e) => {
            if (handleChecked) handleChecked(e.target.checked);
            if (onChange) onChange(e);
          }}
          defaultChecked={defaultChecked}
        />
        <span className="checkbox">
          {isLoading ? (
            <LottieLoading
              size={size === "large" ? 14 : 10}
              color={checked ? "var(--text-gray-normal)" : "var(--pri-blue-4)"}
              backgroundColor="transparent"
            />
          ) : (
            checked && (
              <Icon
                icon="Check"
                size={size === "large" ? 13 : 9.75}
                stroke="--text-gray-normal"
                strokeWidth={1.8}
              />
            )
          )}
        </span>
        {label && <p className="label">{label}</p>}
      </StyledCheckboxLabel>
    );
  }
);
